"use client";

import { useState, useEffect } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import MuiLink from "@mui/material/Link";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import useMediaQuery from "@mui/material/useMediaQuery";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InstagramIcon from "@mui/icons-material/Instagram";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { Link as RouterLink, useLocation } from "react-router-dom";
import YouTubeIcon from "@mui/icons-material/YouTube";
import {
  COMEDYUO_INSTAGRAM_URL,
  COMEDYUO_YOUTUBE_URL,
} from "../_utils/constants";
import Badge from "@mui/material/Badge";
import { useCart } from "../_utils/providers/CartProvider";
import { trackWaitlistClick } from "../_utils/waitlistAnalytics";
import { trackWorkWithUsClick } from "../_utils/workWithUsAnalytics";
import PublicFestivalService, { PublicFestivalListItem } from "../_services/PublicFestivalService";

const headerFont = '"Inter", "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';

/**
 * Header remounts on every page (no shared layout — each `(public)/*\/page.tsx`
 * renders its own <Header/>), so this must be cached at module scope rather than
 * per-mount state, or the "which festivals are published" fetch (and the nav-item
 * pop-in it causes) would re-run on every client-side navigation. Module-level
 * state naturally resets only on a full page load, which is what we want.
 */
let cachedFestivals: PublicFestivalListItem[] | undefined = undefined;
let festivalsPromise: Promise<void> | null = null;

function ensureFestivalsLoaded(): Promise<void> {
  if (cachedFestivals !== undefined) return Promise.resolve();
  if (!festivalsPromise) {
    festivalsPromise = PublicFestivalService.getPublishedFestivals()
      .then((result) => {
        cachedFestivals = result;
      })
      .catch(() => {
        cachedFestivals = [];
      });
  }
  return festivalsPromise;
}

/** Desktop center nav: active route vs dimmed links */
const NAV_LINK_ACTIVE = "#FFFFFF";
const NAV_LINK_INACTIVE = "rgba(255,255,255,0.55)";

/** Stacked ComedyUO wordmark, white on transparent */
const HEADER_LOGO_SRC = "/comedyuo-logo-white.png";

const headerLogoSx = {
  height: { xs: 42, md: 56 },
  width: "auto",
  maxWidth: { xs: 130, md: 170 },
  objectFit: "contain" as const,
  objectPosition: "left center",
  display: "block",
};

type HeaderProps = { variant?: "default" | "dark" };

/** Default `dark` so all public pages share the same wordmark + nav bar unless overridden. */
export default function Header({ variant = "dark" }: HeaderProps) {
  const { pathname } = useLocation();
  const { cart } = useCart();
  const isMobile = useMediaQuery("(max-width:900px)");
  const [open, setOpen] = useState(false);
  const isDark = variant === "dark";
  const [festivals, setFestivals] = useState<PublicFestivalListItem[]>(() => cachedFestivals ?? []);
  const [festivalMenuAnchor, setFestivalMenuAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (cachedFestivals !== undefined) return;
    let cancelled = false;
    ensureFestivalsLoaded().then(() => {
      if (!cancelled) setFestivals(cachedFestivals ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const festivalLinks = festivals.map((f) => ({
    label: f.title,
    href: `/festivals/${f.slug}`,
  }));

  const pressMenuLinks = [
    { label: "Press", href: "/press" },
    { label: "Newsletter", href: "/newsletter" },
  ] as const;

  const baseNavLinks: { label: string; href: string; isNew?: boolean }[] = [
    { label: "About", href: "/about" },
    { label: "Press", href: "/press" },
    { label: "FAQ", href: "/faq" },
    { label: "Private Events", href: "/private-events" },
    { label: "Work With Us", href: "/work-with-us" },
  ];

  const navLinks: { label: string; href: string; isNew?: boolean }[] = [
    ...baseNavLinks.flatMap((link) =>
      link.href === "/press"
        ? [
            { label: "Press", href: "/press" },
            { label: "Newsletter", href: "/newsletter" },
          ]
        : [link]
    ),
    ...festivalLinks,
    { label: "Magic", href: "/magic" },
    { label: "Get Tickets", href: "/tickets" },
    // { label: "Get Photos", href: "/gallery" }, // hidden for now
  ];

  const centerLinks = baseNavLinks;
  const isFestivalRoute = pathname.startsWith("/festivals/");
  const isPressRoute = pathname === "/press" || pathname.startsWith("/newsletter");
  const navLinkSx = {
    fontFamily: headerFont,
    fontWeight: 500,
    fontSize: { md: "0.68rem", lg: "0.74rem", xl: "0.8rem" },
    letterSpacing: { md: "0.03em", xl: "0.04em" },
    textTransform: "uppercase" as const,
    textUnderlineOffset: "6px",
    cursor: "pointer",
    whiteSpace: "nowrap" as const,
    flexShrink: 0,
  };

  const desktopCartButton = (
    <IconButton
      component={RouterLink}
      to="/cart"
      sx={{
        color: "#fff",
        minWidth: 40,
        minHeight: 40,
        p: 0.5,
        backgroundColor: "rgba(255,255,255,0.08)",
        border: "none",
        "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
      }}
      aria-label="Shopping cart"
    >
      <Badge badgeContent={cart?.total_items || 0} color="error">
        <ShoppingBagOutlinedIcon sx={{ fontSize: 22 }} />
      </Badge>
    </IconButton>
  );

  return (
    <>
      <AppBar
        position="relative"
        elevation={0}
        sx={{
          backgroundColor: "#000",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          fontFamily: headerFont,
          zIndex: 1300,
          overflow: "visible",
        }}
      >
        <Toolbar
          sx={{
            position: "relative",
            justifyContent: "space-between",
            alignItems: "center",
            py: { xs: 1.25, md: 2 },
            px: { xs: 1.5, md: 3 },
            minHeight: { xs: 56, md: 72 },
            overflow: "visible",
          }}
        >
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="open navigation menu"
              onClick={() => setOpen(true)}
              sx={{ color: "#fff", mr: 0.5 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* LEFT: Logo (+ COMEDY UO wordmark when not dark) */}
          <Box
            component={RouterLink}
            to="/"
            aria-label="ComedyUO home"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0,
              cursor: "pointer",
              overflow: "visible",
              zIndex: 1,
              textDecoration: "none",
              color: "inherit",
              ...(isDark && !isMobile ? { position: "absolute", left: 24 } : {}),
            }}
          >
            {isDark ? (
              <Box
                component="img"
                src={HEADER_LOGO_SRC}
                alt="ComedyUO"
                sx={headerLogoSx}
              />
            ) : (
              <>
                <Box
                  component="img"
                  src={HEADER_LOGO_SRC}
                  alt="ComedyUO"
                  sx={{ ...headerLogoSx, height: { xs: 32, md: 38 }, mr: 1.25 }}
                />
                <Typography
                  component="span"
                  sx={{
                    fontFamily: headerFont,
                    fontWeight: 700,
                    fontSize: { xs: "1.35rem", md: "1.65rem" },
                    letterSpacing: "0.02em",
                    color: "#fff",
                  }}
                >
                  COMEDY
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: headerFont,
                    fontWeight: 700,
                    fontSize: { xs: "1.35rem", md: "1.65rem" },
                    letterSpacing: "0.02em",
                    color: "rgba(255,255,255,0.75)",
                  }}
                >
                  UO
                </Typography>
              </>
            )}
          </Box>

          {/* CENTER: Nav (desktop) or social + cart (mobile) */}
          {!isMobile ? (
            <Box
              component="nav"
              aria-label="Primary"
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexWrap: "nowrap",
                columnGap: { md: "clamp(6px, 0.9vw, 14px)", lg: "clamp(8px, 1.1vw, 18px)", xl: 2.25 },
                maxWidth: "min(900px, calc(100vw - 360px))",
                px: 0.5,
                zIndex: 2,
                overflow: "visible",
              }}
            >
              {centerLinks.map((link) => {
                const isActive = pathname === link.href;
                if (link.href === "/press") {
                  return (
                    <Box
                      key={link.href}
                      sx={{
                        position: "relative",
                        flexShrink: 0,
                        "&:hover .press-dropdown, &:focus-within .press-dropdown": {
                          opacity: 1,
                          visibility: "visible",
                          pointerEvents: "auto",
                        },
                      }}
                    >
                      <MuiLink
                        component={RouterLink}
                        to="/press"
                        underline="none"
                        sx={{
                          ...navLinkSx,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "2px",
                          color: isPressRoute ? NAV_LINK_ACTIVE : NAV_LINK_INACTIVE,
                        }}
                      >
                        Press
                        <ExpandMoreIcon sx={{ fontSize: "1rem" }} />
                      </MuiLink>
                      <Box
                        className="press-dropdown"
                        sx={{
                          position: "absolute",
                          top: "100%",
                          left: "50%",
                          transform: "translateX(-50%)",
                          pt: 1.25,
                          opacity: 0,
                          visibility: "hidden",
                          pointerEvents: "none",
                          transition: "opacity 0.12s ease",
                          zIndex: 1400,
                        }}
                      >
                        <Box
                          sx={{
                            minWidth: 150,
                            py: 0.75,
                            backgroundColor: "#000",
                            border: "1px solid rgba(255,255,255,0.12)",
                            borderRadius: "8px",
                            boxShadow: "0 12px 28px rgba(0,0,0,0.45)",
                          }}
                        >
                          {pressMenuLinks.map((item) => (
                            <MuiLink
                              key={item.href}
                              component={RouterLink}
                              to={item.href}
                              underline="none"
                              sx={{
                                display: "block",
                                px: 1.75,
                                py: 1,
                                fontFamily: headerFont,
                                fontSize: "0.8rem",
                                fontWeight: 500,
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                                color:
                                  pathname === item.href
                                    ? NAV_LINK_ACTIVE
                                    : NAV_LINK_INACTIVE,
                                "&:hover": {
                                  color: NAV_LINK_ACTIVE,
                                  backgroundColor: "rgba(255,255,255,0.08)",
                                },
                              }}
                            >
                              {item.label}
                            </MuiLink>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  );
                }
                if (link.href === "/work-with-us") {
                  return (
                    <MuiLink
                      key={link.href}
                      component={RouterLink}
                      to={link.href}
                      onClick={trackWorkWithUsClick}
                      underline="none"
                      sx={{
                        ...navLinkSx,
                        color: isActive ? NAV_LINK_ACTIVE : NAV_LINK_INACTIVE,
                      }}
                    >
                      {link.label}
                    </MuiLink>
                  );
                }
                return (
                  <MuiLink
                    key={link.href}
                    component={RouterLink}
                    to={link.href}
                    underline="none"
                    sx={{
                      ...navLinkSx,
                      color: isActive ? NAV_LINK_ACTIVE : NAV_LINK_INACTIVE,
                    }}
                  >
                    {link.label}
                  </MuiLink>
                );
              })}
              {festivalLinks.length === 1 && (
                <MuiLink
                  component={RouterLink}
                  to={festivalLinks[0].href}
                  underline="none"
                  sx={{
                    fontFamily: headerFont,
                    fontWeight: 500,
                    fontSize: { md: "0.68rem", lg: "0.74rem", xl: "0.8rem" },
                    letterSpacing: { md: "0.03em", xl: "0.04em" },
                    textTransform: "uppercase",
                    color: isFestivalRoute ? NAV_LINK_ACTIVE : NAV_LINK_INACTIVE,
                    textUnderlineOffset: "6px",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {festivalLinks[0].label}
                </MuiLink>
              )}
              {festivalLinks.length > 1 && (
                <>
                  <MuiLink
                    component="button"
                    type="button"
                    onClick={(e) => setFestivalMenuAnchor(e.currentTarget)}
                    underline="none"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "2px",
                      fontFamily: headerFont,
                      fontWeight: 500,
                      fontSize: { md: "0.68rem", lg: "0.74rem", xl: "0.8rem" },
                      letterSpacing: { md: "0.03em", xl: "0.04em" },
                      textTransform: "uppercase",
                      color: isFestivalRoute ? NAV_LINK_ACTIVE : NAV_LINK_INACTIVE,
                      textUnderlineOffset: "6px",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      p: 0,
                      border: "none",
                      background: "none",
                    }}
                  >
                    Festivals
                    <ExpandMoreIcon sx={{ fontSize: "1rem" }} />
                  </MuiLink>
                  <Menu
                    anchorEl={festivalMenuAnchor}
                    open={!!festivalMenuAnchor}
                    onClose={() => setFestivalMenuAnchor(null)}
                    slotProps={{
                      paper: {
                        sx: {
                          mt: 1,
                          backgroundColor: "#000",
                          color: "#fff",
                        },
                      },
                    }}
                  >
                    {festivalLinks.map((link) => (
                      <MenuItem
                        key={link.href}
                        component={RouterLink}
                        to={link.href}
                        onClick={() => setFestivalMenuAnchor(null)}
                        sx={{
                          fontFamily: headerFont,
                          color: "#fff",
                          "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
                        }}
                      >
                        {link.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
              <MuiLink
                component={RouterLink}
                to="/magic"
                underline="none"
                sx={{
                  fontFamily: headerFont,
                  fontWeight: 500,
                  fontSize: "0.8rem",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: pathname === "/magic" ? NAV_LINK_ACTIVE : NAV_LINK_INACTIVE,
                  textUnderlineOffset: "6px",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                Magic
              </MuiLink>
              <MuiLink
                component={RouterLink}
                to="/tickets"
                underline="none"
                sx={{
                  fontFamily: headerFont,
                  fontWeight: 500,
                  fontSize: "0.8rem",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: pathname === "/tickets" ? NAV_LINK_ACTIVE : NAV_LINK_INACTIVE,
                  textUnderlineOffset: "6px",
                  cursor: "pointer",
                }}
              >
                Get Tickets
              </MuiLink>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
                justifyContent: "center",
              }}
            >
              <IconButton
                href={COMEDYUO_YOUTUBE_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "#fff" }}
              >
                <YouTubeIcon style={{ fontSize: "38px" }} />
              </IconButton>
              <IconButton
                href={COMEDYUO_INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "#fff" }}
              >
                <InstagramIcon style={{ fontSize: "28px" }} />
              </IconButton>
              <IconButton
                component={RouterLink}
                to="/cart"
                aria-label="Shopping cart"
                sx={{ color: "#fff" }}
              >
                <Badge badgeContent={cart?.total_items || 0} color="error">
                  <ShoppingBagOutlinedIcon sx={{ fontSize: 24 }} />
                </Badge>
              </IconButton>
            </Box>
          )}

          {/* RIGHT: cart (desktop) */}
          <Box
            sx={{
              position: "absolute",
              right: 12,
              display: isDark && isMobile ? "none" : "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            {!isMobile && desktopCartButton}
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer anchor="left" open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            width: "100vw",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#0a0a0a",
            fontFamily: headerFont,
          }}
        >
          <Box
            sx={{
              position: "relative",
              py: 2,
              px: 2,
              borderBottom: "1px solid rgba(255,255,255,0.1)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              ...(isDark ? { overflow: "visible" } : {}),
            }}
          >
            <IconButton onClick={() => setOpen(false)} sx={{ color: "#fff" }}>
              <CloseIcon sx={{ fontSize: 28 }} />
            </IconButton>
            <Box
              component={RouterLink}
              to="/"
              aria-label="ComedyUO home"
              onClick={() => setOpen(false)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0,
                cursor: "pointer",
                flex: 1,
                justifyContent: "center",
                minWidth: 0,
                px: 1,
                overflow: "visible",
                textDecoration: "none",
                color: "inherit",
              }}
            >
              {isDark ? (
                <Box
                  component="img"
                  src={HEADER_LOGO_SRC}
                  alt="ComedyUO"
                  sx={{
                    ...headerLogoSx,
                    height: 36,
                    maxWidth: "min(130px, 35vw)",
                    objectPosition: "center",
                  }}
                />
              ) : (
                <>
                  <Box
                    sx={{
                      backgroundColor: "#000",
                      borderRadius: "50%",
                      overflow: "hidden",
                      minWidth: 48,
                      minHeight: 48,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 0.5,
                      mr: 1,
                      "& img": { height: 42, width: "auto", objectFit: "contain" },
                    }}
                  >
                    <Box component="img" src={HEADER_LOGO_SRC} alt="ComedyUO" />
                  </Box>
                  <Typography component="span" sx={{ fontFamily: headerFont, fontWeight: 700, fontSize: "1.1rem", color: "#fff" }}>
                    COMEDY
                  </Typography>
                  <Typography component="span" sx={{ fontFamily: headerFont, fontWeight: 700, fontSize: "1.1rem", color: "rgba(255,255,255,0.75)" }}>
                    UO
                  </Typography>
                </>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton href={COMEDYUO_INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" sx={{ color: "#fff" }}>
                <InstagramIcon sx={{ fontSize: 26 }} />
              </IconButton>
              <IconButton
                component={RouterLink}
                to="/cart"
                aria-label="Shopping cart"
                onClick={() => setOpen(false)}
                sx={{ color: "#fff" }}
              >
                <Badge badgeContent={cart?.total_items || 0} color="error">
                  <ShoppingBagOutlinedIcon sx={{ fontSize: 22 }} />
                </Badge>
              </IconButton>
            </Box>
          </Box>
          <Box
            component="nav"
            aria-label="Primary"
            sx={{ flex: 1, py: 1, display: "flex", flexDirection: "column" }}
          >
            {navLinks.map((link) => {
              const isActive = pathname === link.href;
              return (
                <Box
                  key={link.href}
                  component={RouterLink}
                  to={link.href}
                  onClick={() => {
                    if (link.href === "/work-with-us") trackWorkWithUsClick();
                    setOpen(false);
                  }}
                  sx={{
                    px: 3,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                    textDecoration: "none",
                    color: "inherit",
                    backgroundColor: isActive ? "rgba(255,255,255,0.1)" : "transparent",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: headerFont,
                      fontSize: "1rem",
                      fontWeight: 500,
                      color: isActive ? NAV_LINK_ACTIVE : NAV_LINK_INACTIVE,
                    }}
                  >
                    {link.label}
                  </Typography>
                  {link.isNew && (
                    <Box sx={{ backgroundColor: "#ec407a", color: "#fff", fontSize: 10, px: 0.8, borderRadius: 0.5 }}>
                      new
                    </Box>
                  )}
                </Box>
              );
            })}
            <Box
              component={RouterLink}
              to="/waitlist"
              onClick={() => {
                trackWaitlistClick();
                setOpen(false);
              }}
              sx={{
                px: 3,
                py: 1.5,
                cursor: "pointer",
                textDecoration: "none",
                color: "inherit",
                backgroundColor: pathname === "/waitlist" ? "rgba(255,255,255,0.1)" : "transparent",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
              }}
            >
              <Typography
                sx={{
                  fontFamily: headerFont,
                  fontSize: "1rem",
                  fontWeight: 500,
                  color: pathname === "/waitlist" ? NAV_LINK_ACTIVE : NAV_LINK_INACTIVE,
                }}
              >
                Join our waitlist
              </Typography>
            </Box>
          </Box>

          <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.1)", py: 2, px: 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                color: "rgba(255,255,255,0.8)",
              }}
              onClick={() => {
                window.location.assign(
                  "https://shopify.com/authentication/63905923251/login?client_id=bfd59bc3-5131-46ea-ab5f-3d5f5df821db&locale=en&redirect_uri=%2Fauthentication%2F63905923251%2Foauth%2Fauthorize%3Fclient_id%3Dbfd59bc3-5131-46ea-ab5f-3d5f5df821db%26locale%3Den%26nonce%3Dc5bb313f-24f2-4865-9f63-f43f5033e2cc%26redirect_uri%3Dhttps%253A%252F%252Fshopify.com%252F63905923251%252Faccount%252Fcallback%26region_country%3DUS%26response_type%3Dcode%26scope%3Dopenid%2Bemail%2Bcustomer-account-api%253Afull%26state%3DhWN7f7bcYJihnHPibBMVDqoD&region_country=US"
                );
                setOpen(false);
              }}
            >
              <PersonOutlineIcon fontSize="small" />
              <Typography sx={{ fontFamily: headerFont, fontSize: 14 }}>Log in</Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
